<template>
  <div :class="{ 'container': !useBlock }">
    <div v-if="header && Object.keys(products).length > 0" class="product-list__header">
      {{ header }}
    </div>

    <oie-product-filter-bar
      v-if="showFilter"
      :use-compare="useCompare"
      :current-count="sortedAndFilteredProducts.length"
      :show-sort-order="showSortOrder"
      :total-count="totalCount"
      :show-fraction-filter="showFractionFilter"
      :search-query.sync="searchQuery"
      :enable-search="enableSearch"
      :show-facet-filter-toggle="showFacetFilterToggle"></oie-product-filter-bar>

    <div v-if="totalCount === 0 && searchQuery" class="product-list__no-result">
      <p class="product-list__no-result-title">
        {{ tStatic('noListResultTitle') }}
      </p>
      <p class="product-list__no-result-text">
        {{ tStatic('noListResultText') }}
      </p>
    </div>

    <oie-product-filter
      v-if="showMatchingFilter"
      :show-fraction-filter="showFractionFilter"
      :header="tStatic('vehicleFilterFractionTitle')"
      :text="tStatic('vehicleFilterFractionDescription')"></oie-product-filter>
    <div :class="['product-list__products-filter-wrapper', { 'section-block--create-space-top': showFilter }]">
      <slot name="filters"></slot>

      <oie-section-block-shell :css-classes="['section-block--create-space', 'section-block--no-padding']">
        <oie-layout-grid
          id="product-list-page-wrapper"
          ref="productListPageWrapper"
          :class="{ 'grid--list': useListViewComputed && !useGrid, 'grid--grid': !useListViewComputed || useGrid, 'element--is-loading element--is-loading--at-top': loading }"
          css-classes="grid--four">
          <template v-for="(product,i) in sortedAndFilteredProducts">
            <oie-product-shell
              :key="`product-loop-${i}`"
              :class="[{ 'product-shell--show-variants': isExpanded(product.id) }, { 'product-shell--showing-technical-information': isTechnicalInformationBlockActive(product.id) }]">
              <oie-product-card
                :show-filter="showFilter"
                :show-fraction-filter="showFractionFilter"
                :product="product"
                :index="i"
                :use-compressed-view="useCompressedView"
                :type-of-listing="typeOfListing"
                :is-replacement="isReplacement"></oie-product-card>

              <oie-product-technical-information v-if="(useListViewComputed || mq('to-small') ) && Object.keys(product.specification).length > 0" slot="technical-information" :product-id="product.id">
                <div class="grid grid--four techinical-facts__grid">
                  <template v-for="(technicalSpec, categoryName) in product.specification">
                    <div :key="categoryName" class="property-container">
                      <div class="property-container__header">
                        {{ categoryName }}
                      </div>

                      <table class="property-table technical-facts__table">
                        <tr v-for="(value, key) in technicalSpec" :key="key" class="property-table__item">
                          <td>
                            {{ key }}
                          </td>

                          <td>
                            {{ value }}
                          </td>
                        </tr>
                      </table>
                    </div>
                  </template>
                </div>
              </oie-product-technical-information>

              <!-- fractions node pages -->
              <template v-if="fractions.length > 0">
                <oie-product-fraction v-show="showFractionFilter" :product-id="product.id"></oie-product-fraction>
              </template>
            </oie-product-shell>
            <oie-product-variant-shell
              v-show="isExpanded(product.id)"
              :key="`product-loop-${i}--variants`"
              :index="i"
              :title="product.name"
              :card-id="product.id">
              <template v-slot:default="slotProps">
                <oie-product-variants
                  :apply-article-number-shadow="slotProps.applyArticleNumberShadow"
                  :apply-stock-price-shadow="slotProps.applyStockPriceShadow"
                  :product="product"
                  :type-of-listing="typeOfListing"
                  :is-replacement="isReplacement"></oie-product-variants>
              </template>
            </oie-product-variant-shell>
            <oie-cross-link-banner
              v-if="showCrossLinkBanner(i)"
              :key="`cross-link-${i}`"
              :banner="banner"
              :product-count="sortedAndFilteredProducts.length"></oie-cross-link-banner>
          </template>

          <template v-for="filler of 3">
            <div :key="`filler-${filler}`" class="grid__item--filler"></div>
          </template>
        </oie-layout-grid>

        <!-- The button and the SEO text live inside the shell so they line up with the products.
             The wrapper above is a flex row - filters on the left, this shell on the right - so
             anything placed outside it starts at the far left of the page instead (OC-2623). -->
        <oie-base-button
          v-if="(totalCount > count && !hideShowMoreLink)"
          css-classes="button--blue-no-border button-link product-list__button product-list__button--after-grid"
          :text="tStatic('showMoreLink')"
          @click.native="loadMore(allFilterParameters)"></oie-base-button>

        <!-- After the button on purpose: a long text used to push it so far down the page that it
             read as the end of the list rather than a way to see more of it (OC-2623). -->
        <slot name="after-products"></slot>
      </oie-section-block-shell>
    </div>
  </div>
</template>

<script>
import { mapGetters, mapActions } from 'vuex'
import httpService from '@/core/http-service'
import _ from 'lodash'
import priceService from '@/core/price-service'
import utils from '@/core/utils'
import fractionService from '@/core/fraction-service'
// eslint-disable-next-line no-unused-vars
import { state as filterState, storeInitialFacetOptions, selectedFilters, setFilterQueryParams, applyFilterQueryParams, storeFilters, getUrlFilterQueryParams, allFilterParameters, getCachedInitialFilters, checkLastFacetUsed, getCachedLatestFilters, hasSelectedFilters } from '@/store/filters'

export default {
  props: {
    rememberScroll: {
      type: Boolean,
      default: true
    },
    header: {
      type: String,
      default: ''
    },
    showMoreProducts: {
      type: Boolean,
      default: true
    },
    showFilter: {
      type: Boolean,
      default: true
    },
    showMatchingFilter: {
      type: Boolean,
      default: true
    },
    showFractionFilter: {
      type: Boolean,
      default: true
    },
    useGrid: {
      type: Boolean,
      default: false
    },
    isNewsBlock: {
      type: Boolean,
      default: false
    },
    category: {
      type: String,
      default: ''
    },
    vehicleCategory: {
      type: String,
      default: ''
    },
    sparePartNode: {
      type: String,
      default: ''
    },
    relatedProducts: {
      type: Array,
      default: null
    },
    isSkuList: {
      type: Boolean,
      default: false
    },
    pageSize: {
      type: Number,
      default: 40
    },
    useCompare: {
      type: Boolean,
      default: true
    },
    useBlock: {
      type: Boolean,
      default: false
    },
    useCompressedView: {
      type: Boolean,
      default: false
    },
    hideShowMoreLink: {
      type: Boolean,
      default: false
    },
    cacheProducts: {
      type: Boolean,
      default: false
    },
    ignoreRelated: {
      type: Boolean,
      default: false
    },
    completeProducts: {
      type: Boolean,
      default: false
    },
    showSortOrder: {
      type: Boolean,
      default: true
    },
    typeOfListing: {
      type: String,
      default: ''
    },
    isReplacement: {
      type: Boolean,
      default: false
    },
    enableSearch: {
      type: Boolean,
      default: false
    },
    enableSpecificationFacets: {
      type: Boolean,
      default: false
    },
    showFacetFilterToggle: {
      type: Boolean,
      default: false
    },
    cachePrices: {
      type: Boolean,
      default: false
    }
  },
  data: () => ({
    technicalInformation: [],
    filters: [],
    productExpanded: null,
    products: [],
    page: 0,
    totalCount: 0,
    count: 0,
    sortBy: null,
    madeReset: false,
    searchQuery: '',
    banner: null,
    loading: false
  }),
  computed: {
    ...mapGetters('ui', ['useListView', 'forceListView', 'forceGridView', 'showTechnicalnformation', 'useVat', 'isTechnicalInformationBlockActive']),
    ...mapGetters('fractions', ['selectedFractionTypes']),
    ...mapGetters('productVariants', ['isExpanded', 'displayAllVariants']),
    loadMoreProperties() {
      return [this.sortBy, _.now]
    },
    fractions() {
      return this.$store.getters['fractions/allFractions']
    },
    sortedAndFilteredProducts() {
      let result = this.products
      if(this.showMatchingFilter && this.selectedFractionTypes.length > 0) {
        result = _.filter(result, p => {
          const selection = fractionService.hasSelectedMatchingFractions(p.id)
          return (selection.partialMatch || selection.noItemFractions || selection.matchesOneOfAll) && !selection.hasMismatchSelected
        })
      }
      if(this.isNewsBlock) {
        result = _.orderBy(result, 'newsPriority')
      }
      return result
    },
    useListViewComputed() {
      if (this.forceListView) { return true }
      if (this.forceGridView) { return false }
      if (this.useGrid && !this.showFilter) {
        return false
      }
      return this.useListView
    },
    selectedFilters() { return selectedFilters() },
    allFilterParameters() { return allFilterParameters() }
  },
  watch: {
    searchQuery: _.debounce(function (newVal, oldVal) {
      if (!this.enableSearch) {
        return
      }
      if(newVal || oldVal) {
        utils.setQueryStringParameter('query', encodeURIComponent(newVal))
        this.page = 0
        this.products = []
        this.loadMore()
      }
    }, 500),
    products(value) {
      this.publish('product-list-page-wrapper.products-updated', value)
    },
    loadMoreProperties() {
      this.loadMore()
    },
    useVat: {
      immediate: true,
      handler() {
        const listProductIds = _.flatMap(this.products, 'id')
        const replacementProductIds = _.flatMap(_.flatMap(this.products, p => _.flatMap(p.replacementProducts)), 'id')
        const productIds = _.uniq([...listProductIds, ...replacementProductIds])

        if (productIds && productIds.length) {
          httpService.post('productListPrices', { productId: productIds }, { fromProductsBlock: this.cachePrices }).then((response) => {
            priceService.populatePricesFromBackend(response.data, this.products)
          })
        }
      }
    },
    'clientModel.useListPrice': {
      immediate: true,
      handler() {
        const listProductIds = _.flatMap(this.products, 'id')
        const replacementProductIds = _.flatMap(_.flatMap(this.products, p => _.flatMap(p.replacementProducts)), 'id')
        const productIds = _.uniq([...listProductIds, ...replacementProductIds])

        if (productIds && productIds.length) {
          httpService.post('productListPrices', { productId: productIds }, { fromProductsBlock: this.cachePrices }).then((response) => {
            priceService.populatePricesFromBackend(response.data, this.products)
          })
        }
      }
    },
    useListViewComputed() {
      this.toggleShowAllVariants(false)
      this.publish('toggle.articles.all', false)
      if (!this.useListViewComputed) {
        this.$nextTick(function () {
          this.resetElementHeightBundle()
          this.setElementHeightsBundle()
        })
      } else {
        this.resetElementHeightBundle()
      }
    },
    selectedFractionTypes() {
      this.$nextTick(function () {
        this.resetElementHeightBundle()
        this.setElementHeightsBundle()
      })
    }
  },
  async mounted() {
    this.page = (parseInt(utils.getQueryStringParam('p')) - 1) || 0

    this.getCrossLinkBanner()

    if(this.enableSpecificationFacets) {
      this.initializeFilters()
      await this.loadMore(getUrlFilterQueryParams())
    } else {
      await this.loadMore()
    }

    this.subscribe('dropdown-option-selected-sort-products', selectedOption => {
      this.products = []
      this.page = 0
      this.sortBy = selectedOption.value
    })

    this.subscribe('product.wrapper.element.eq', () => {
      requestAnimationFrame(() => {
        this.resetElementHeightBundle()
        this.setElementHeightsBundle()
      })
    })

    // what is the purpose of this?
    // this.subscribe('toggle.articles.set.height', (isExpanded) => {
    //   if (isExpanded) {
    //     this.resetElementHeight('.product-card')
    //   } else {
    //     this.setElementHeight('.product-card')
    //   }
    // })

    this.subscribe('toggle.articles', (id) => {
      this.productExpanded = id
    })

    _.forEach(['resize', 'orientationchange'], event => {
      window.addEventListener(event, _.throttle(() => {
        if (!this.useListViewComputed && this.mq('small')) {
          this.setElementHeightsBundle()

          this.madeReset = false
        } else {
          if (!this.madeReset) {
            this.resetElementHeightBundle()

            this.madeReset = true
          }
        }
      }, 400))
    })

    if(this.rememberScroll) {
      utils.rememberScroll(document.location.href, '.product-shell')
    }

    this.searchQuery = utils.getQueryStringParam('query') || ''

    if (!this.showFilter) {
      this.changeForceListView()
    }
  },
  methods: {
    ...mapActions('ui', ['toggleValue']),
    ...mapActions('productVariants', ['openAllVariants', 'displayAllVariantRows', 'toggleShowAllVariants']),
    getScrollCacheKey() {
      return `productscroll_${window.location.href}`
    },
    setProductListPrices(productIds) {
      httpService.post('productListPrices', { productId: productIds }, { fromProductsBlock: this.cachePrices }).then((response) => {
        this.setElementHeightsBundle()
        priceService.populatePricesFromBackend(response.data, this.products)
      })
    },
    getLoadMoreUrl() {
      // Include stock debug query params if present (used only by CmsAdmins for testing)
      const queryParams = new URLSearchParams()
      const currentStock = utils.getQueryStringParam('currentStock')
      const isOrderItem = utils.getQueryStringParam('isOrderItem')
      const deliveryTime = utils.getQueryStringParam('deliveryTime')
      const deliveryDate = utils.getQueryStringParam('deliveryDate')

      if (currentStock) queryParams.append('currentStock', currentStock)
      if (isOrderItem) queryParams.append('isOrderItem', isOrderItem)
      if (deliveryTime) queryParams.append('deliveryTime', deliveryTime)
      if (deliveryDate) queryParams.append('deliveryDate', deliveryDate)

      const stockParams = queryParams.toString() ? `?${queryParams.toString()}` : ''

      if(this.category && !this.sparePartNode) {
        return 'categories/' + this.category + '/products' + stockParams
      }
      if (this.vehicleCategory && this.sparePartNode) {
        return 'categories/' + this.sparePartNode + '/vehicle/' + this.vehicleCategory + '/products' + stockParams
      }

      if(_.isEmpty(this.relatedProducts)) return

      if(!this.ignoreRelated) {
        return 'relatedProducts'
      }
      if(this.completeProducts) {
        return 'completeProducts'
      }
      return 'products'
    },
    getLoadMoreBody(filters) {
      const defaultBody = {
        page: this.page,
        pageSize: this.getProductListPageSize(this.pageSize),
        query: this.searchQuery,
        ...filters
      }

      if((this.category && !this.sparePartNode) || (this.vehicleCategory && this.sparePartNode)) {
        defaultBody.sortBy = this.sortBy
      } else {
        defaultBody.productIds = this.relatedProducts
        // Always set removeDuplicates to true when we have productIds (related products)
        if (this.relatedProducts && Array.isArray(this.relatedProducts) && this.relatedProducts.length > 0) {
          defaultBody.removeDuplicates = true
        } else {
          defaultBody.removeDuplicates = this.isSkuList
        }
      }

      return defaultBody
    },
    async loadMore(filters = {}) {
      this.loading = true
      this.page++
      const url = this.getLoadMoreUrl()
      if(!url) return
      const body = this.getLoadMoreBody(filters)
      const response = await httpService.post(url, body)
      this.totalCount = response.data.totalCount
      this.products = this.products.concat(response.data.data)
      this.count = this.products.length
      const listProductIds = _.flatMap(response.data.data, 'id')
      const replacementProductIds = _.flatMap(_.flatMap(response.data.data, p => _.flatMap(p.replacementProducts)), 'id')
      const productIds = _.uniq([...listProductIds, ...replacementProductIds])
      this.setProductListPrices(productIds)
      this.loading = false
    },
    async getFilters(filters = {}) {
      filterState.fetchingFilters = true
      const url = this.getLoadMoreUrl()
      if(url) {
        const cachedFilters = getCachedLatestFilters(url, filters)
        if(cachedFilters) {
          filterState.fetchingFilters = false
          return cachedFilters
        } else {
          const body = { includeFacets: true, query: this.searchQuery, pageSize: 0, page: 1, ...filters }
          const response = await httpService.post(url, body)
          filterState.fetchingFilters = false
          return response.data
        }
      }
    },
    async setInitialFilters() {
      const cachedInitialFilters = getCachedInitialFilters(this.getLoadMoreUrl())
      if(cachedInitialFilters) {
        storeInitialFacetOptions(cachedInitialFilters)
        storeFilters(cachedInitialFilters)
      } else {
        const filters = await this.getFilters()
        storeInitialFacetOptions(filters)
        storeFilters(filters)
        sessionStorage.setItem('initialFilters', JSON.stringify({ key: this.getLoadMoreUrl(), data: filters }))
      }
      applyFilterQueryParams()
    },
    async initializeFilters() {
      await this.setInitialFilters()
      checkLastFacetUsed()
      // Has pre-existing filters when coming to page? -> refetch for updated filters
      if(hasSelectedFilters()) {
        const filters = await this.getFilters(allFilterParameters())
        storeFilters(filters)
      }

      this.$watch('selectedFilters', async (newVal, oldVal) => {
        if(!_.isEqual(newVal, oldVal)) {
          setFilterQueryParams()
          this.products = []
          this.page = 0
          this.loadMore(allFilterParameters())
          const filters = await this.getFilters(allFilterParameters())
          storeFilters(filters)
        }
      }, { deep: true })

      filterState.filtersInitialized = true
    },
    getCrossLinkBanner() {
      const category = this.category && !this.sparePartNode ? this.category : this.sparePartNode
      if (category !== '') {
        httpService.get(`categories/${category}/crossLinkBanner`).then(res => {
          if (res.data.crossLink) {
            this.banner = res.data.crossLink
          }
        })
      }
    },
    setElementHeight (selector, timeout = false) {
      const wrapper = this.$refs['productListPageWrapper']
      const grid = Array.from(wrapper.$el.children)

      const baseOffset = grid[0].offsetTop
      const breakIndex = grid.findIndex(item => item.offsetTop > baseOffset)
      const numPerRow = (breakIndex === -1 ? grid.length : breakIndex)
      const element = wrapper.$el.querySelectorAll(selector)

      setTimeout(() => {
        for (let i = 0; i < element.length; i = i + numPerRow) {
          let height = 0

          for (let j = i; j < i + numPerRow; j++) {
            if (j < element.length) {
              let cardHeight = element[j].scrollHeight

              if (selector === '.product-card') {
                cardHeight = cardHeight + 2
              }

              if (cardHeight > height) {
                height = cardHeight
              }
            }
          }

          for (let j = i; j < i + numPerRow; j++) {
            if (j < element.length) {
              element[j].style.height = `${height}px`
            }
          }
        }
      }, timeout ? 400 : 0)
    },
    setElementHeightsBundle() {
      if (!this.useListViewComputed && this.mq('small')) {
        this.setElementHeight('.product-card__name')
        this.setElementHeight('.product-card__descriptions')
        this.setElementHeight('.product-card__article-number')
        this.setElementHeight('.product-card__manufacturer-logo')
        this.setElementHeight('.product-card__actions', true)

        if (this.fractions.length > 0) {
          this.setElementHeight('.product-card__fractions-information')
          this.setElementHeight('.product-fraction')
        }
      }
    },
    resetElementHeight (selector) {
      _.forEach(document.querySelectorAll(selector), (element) => {
        element.style.height = ''
      })
    },
    resetElementHeightBundle() {
      this.resetElementHeight('.product-card')
      this.resetElementHeight('.product-card__name')
      this.resetElementHeight('.product-card__descriptions')
      this.resetElementHeight('.product-card__article-number')
      this.resetElementHeight('.product-card__actions')

      if (this.fractions.length > 0) {
        this.resetElementHeight('.product-card__fractions-information')
        this.resetElementHeight('.product-fraction')
      }
    },
    hasFractions(productId) {
      return this.showFilter && fractionService.getItemFractions(productId)
    },
    searchHasSelectedHits(items) {
      if (items === null || items.length <= 1) {
        return false
      }

      return items.filter((p) => {
        return p.selected
      }).length > 0
    },
    showCrossLinkBanner(i) {
      if (!this.banner) return false
      const productCount = this.sortedAndFilteredProducts.length

      if (this.useListView) {
        return (productCount <= 2 && i === productCount - 1) || i === 1
      } else {
        return (productCount <= 6 && i === productCount - 1) || i === 5
      }
    },
    changeForceListView() {
      if (this.mq('to-medium')) {
        this.toggleValue({ key: 'forceView', data: null })
      }
    }
  }
}
</script>

<style lang="scss">
#product-list-page-wrapper {
  position: relative;
  min-height: 7rem;
}

.product-list__header {
  @include h3-bold;
  padding-bottom: measure(half);
}

.product-list__products-filter-wrapper {
  display: flex;
  gap: 10px;
  align-items: flex-start;
}

.product-list__seo-text {
  margin-top: measure(full);
}

.product-list__button {
  display: flex;
  justify-content: center;
  margin: 0 auto;
  min-width: 12.5rem;
}

// The button used to sit outside the section block and was separated from the products by that
// block's own bottom margin. It is inside the block now, so it carries the gap itself. Written with
// both classes because .product-list__button is declared globally in two other list components as
// well, and its margin shorthand would otherwise win on load order alone (OC-2623).
.product-list__button.product-list__button--after-grid {
  margin-top: measure(double);
}

.product-list__no-result {
  text-align: center;
  background-color: color(blue-6);
  border: 1px solid color(blue-1);
  margin-top: measure(quarter);
  margin-bottom: measure(quarter);
  padding: measure(quarter);
}

.product-list__no-result-title {
  @include h2-semi-bold;
  color: color(blue-1);
}

.product-card__header em {
    background-color: #44B1FA;
    font-style: normal;
    color: white;
    padding: 0rem .1rem 0rem .05rem;
}

.product-card__header em.instant-result__complete_match {
    background-color: #006db6;
    padding: .15rem .4rem .15rem .4rem;
    border-radius: 2px;
}
</style>
